import { motion } from "framer-motion";
import { Building2, ChevronRight } from "lucide-react";
import { RouterOutput, trpc } from "../../utils/trpc";
import { useConnectDispatch, useConnectState } from "../ConnectContext";
import ConnectStep from "../components/ConnectStep";
import { ActionType } from "../types";
import { getBankTheme } from "@/lib/bank-theme";

// Skeleton for loading state
const InstitutionSkeleton = ({ index }: { index: number }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: index * 0.05 }}
    className="p-4 rounded-xl border border-border/50 bg-card"
  >
    <div className="flex items-start gap-3">
      <div className="h-5 w-5 rounded bg-muted animate-pulse" />
      <div className="flex-1 space-y-2">
        <div className="h-5 w-32 bg-muted rounded animate-pulse" />
        <div className="h-4 w-24 bg-muted rounded animate-pulse" />
      </div>
    </div>
  </motion.div>
);

const InstitutionSelectStep = () => {
  const { connectSessionId } = useConnectState();
  const institutionsQuery = trpc.connect.getInstitutions.useQuery({
    connectSessionId: connectSessionId!,
  });

  const dispatch = useConnectDispatch();

  const handleSelectInstitution = (
    institution: RouterOutput["connect"]["getInstitutions"]["institutions"][0],
  ) => {
    dispatch({
      type: ActionType.SetInstitution,
      payload: {
        id: institution.id,
        name: institution.name,
        domain: institution.domain || "",
        iconUrl: institution.iconUrl || undefined,
        authFields: institution.authFields,
        primaryColor: institution.primaryColor,
        primaryForeground: institution.primaryForeground,
        accentColor: institution.accentColor,
        surfaceColor: institution.surfaceColor,
      },
    });
  };

  return (
    <ConnectStep stepId="institution-select">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-xl font-semibold tracking-tight">
            Selecciona tu banco
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Elige la institución con la que deseas conectar
          </p>
        </div>

        {/* Institution list */}
        <div className="flex-1 overflow-y-auto -mx-5 px-5 space-y-2">
          {institutionsQuery.isLoading && (
            <>
              {[0, 1, 2, 3].map((i) => (
                <InstitutionSkeleton key={i} index={i} />
              ))}
            </>
          )}

          {!institutionsQuery.isLoading &&
            institutionsQuery.data?.institutions.map((ins, index) => {
              const theme = getBankTheme(ins);
              return (
                <motion.button
                  key={ins.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{
                    duration: 0.2,
                    delay: index * 0.04,
                  }}
                  className="w-full p-4 rounded-xl border border-border/60 bg-card hover:border-border hover:shadow-sm active:scale-[0.98] transition-all duration-150 text-left group"
                  onClick={() => handleSelectInstitution(ins)}
                  data-ph-capture-attribute-step="institution-select"
                  data-ph-capture-attribute-institution={ins.id}
                >
                  <div className="flex items-center gap-3">
                    {/* Color accent icon */}
                    <div
                      className="flex-shrink-0"
                      style={{ color: theme.primary }}
                    >
                      <Building2 className="h-5 w-5" />
                    </div>

                    {/* Content */}
                    <span className="flex-1 font-medium text-foreground">
                      {ins.name}
                    </span>

                    <ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-muted-foreground group-hover:translate-x-0.5 transition-all" />
                  </div>
                </motion.button>
              );
            })}
        </div>

        {/* Footer disclaimer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex-shrink-0 pt-4 text-center"
        >
          <p className="text-xs text-muted-foreground/70 leading-relaxed">
            Todas las marcas registradas son propiedad de sus respectivos
            dueños. Su uso no representa un endorso ni afiliación con Bridge.
          </p>
        </motion.div>
      </div>
    </ConnectStep>
  );
};

export default InstitutionSelectStep;
